import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPECTATIONS = [
  {
    n: "01",
    text: "We read every enquiry carefully. Expect a considered reply, not an automated sequence.",
  },
  {
    n: "02",
    text: "The first conversation focuses on the outcome you are seeing and where it surfaces — not on selling you anything.",
  },
  {
    n: "03",
    text: "If we are not the right fit, we will say so — and suggest what might help instead.",
  },
];

const inputClass =
  "w-full border border-line bg-white px-4 py-3.5 text-sm text-ink placeholder:text-slate-400 focus:border-ink focus:outline-none transition-colors duration-200";

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    challenge: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [reference, setReference] = useState("");
  const [error, setError] = useState("");

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      const res = await axios.post(`${API}/enquiries`, form);
      setReference(res.data.id.slice(0, 8).toUpperCase());
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(
        err.response?.data?.detail
          ? "Please check the highlighted fields and try again."
          : "Something went wrong sending your enquiry. Please try again."
      );
    }
  };

  return (
    <>
      <Seo
        title="Discuss a System — Improve Systems"
        siteName="Improve Systems"
        description="Tell us about the operational outcome you are seeing. We will help you trace the system behind it."
      />
      <section data-testid="contact-section" className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(15,23,42,0.045) 1px, transparent 1px)",
            backgroundSize: "25% 100%",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto grid max-w-7xl gap-16 px-6 pb-24 pt-32 sm:pt-40 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="mb-6 flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-slate-500">
                <span className="h-px w-10 bg-slate-300" aria-hidden="true" />
                CONTACT
              </p>
              <h1 className="font-serif text-4xl leading-[1.06] tracking-tight text-ink sm:text-5xl">
                Discuss a System
              </h1>
              <p className="mt-8 max-w-md leading-relaxed text-slate-600">
                Tell us about the outcome you are seeing — a backlog that will not clear, an
                error that keeps returning, a process that underperforms despite investment.
                We will help you trace it.
              </p>
            </Reveal>
            <div className="mt-12 border-t border-line">
              {EXPECTATIONS.map((step, i) => (
                <Reveal key={step.n} delay={0.1 + i * 0.07}>
                  <div className="flex gap-6 border-b border-line py-5">
                    <span className="font-mono text-[11px] text-slate-400">{step.n}</span>
                    <p className="text-sm leading-relaxed text-slate-600">{step.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              {status === "success" ? (
                <div
                  data-testid="contact-success"
                  className="flex h-full flex-col items-start justify-center border border-line bg-white p-10 sm:p-14"
                >
                  <CheckCircle2 className="h-8 w-8 text-accent" aria-hidden="true" />
                  <p className="mt-6 font-mono text-[11px] tracking-[0.3em] text-slate-500">
                    ENQUIRY RECEIVED — REF {reference}
                  </p>
                  <h2 className="mt-4 font-serif text-3xl text-ink">Thank you.</h2>
                  <p className="mt-4 max-w-md leading-relaxed text-slate-600">
                    Your note has been recorded. We review every enquiry personally and will
                    be in touch to continue the conversation.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="contact-form"
                  className="border border-line bg-white p-8 sm:p-10"
                >
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] tracking-[0.25em] text-slate-500">
                        NAME *
                      </label>
                      <input
                        id="contact-name"
                        data-testid="contact-name-input"
                        required
                        value={form.name}
                        onChange={update("name")}
                        className={inputClass}
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] tracking-[0.25em] text-slate-500">
                        EMAIL *
                      </label>
                      <input
                        id="contact-email"
                        data-testid="contact-email-input"
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        className={inputClass}
                        placeholder="you@organization.gov"
                      />
                    </div>
                  </div>
                  <div className="mt-6">
                    <label htmlFor="contact-organization" className="mb-2 block font-mono text-[10px] tracking-[0.25em] text-slate-500">
                      ORGANIZATION
                    </label>
                    <input
                      id="contact-organization"
                      data-testid="contact-organization-input"
                      value={form.organization}
                      onChange={update("organization")}
                      className={inputClass}
                      placeholder="Agency, company, or program"
                    />
                  </div>
                  <div className="mt-6">
                    <label htmlFor="contact-challenge" className="mb-2 block font-mono text-[10px] tracking-[0.25em] text-slate-500">
                      WHICH SYSTEM OR OUTCOME IS ON YOUR MIND? *
                    </label>
                    <textarea
                      id="contact-challenge"
                      data-testid="contact-challenge-input"
                      required
                      rows={4}
                      value={form.challenge}
                      onChange={update("challenge")}
                      className={inputClass}
                      placeholder="Describe what you are observing — the backlog, the error, the recurring issue — and where it surfaces."
                    />
                  </div>
                  <div className="mt-6">
                    <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] tracking-[0.25em] text-slate-500">
                      ANYTHING ELSE WE SHOULD KNOW
                    </label>
                    <textarea
                      id="contact-message"
                      data-testid="contact-message-input"
                      rows={3}
                      value={form.message}
                      onChange={update("message")}
                      className={inputClass}
                      placeholder="Context, constraints, timelines — optional."
                    />
                  </div>
                  {status === "error" && (
                    <p data-testid="contact-error" className="mt-6 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={status === "submitting"}
                    className="group mt-8 inline-flex items-center gap-2 bg-navy px-8 py-4 text-sm tracking-wide text-white transition-colors duration-300 hover:bg-accent-deep disabled:cursor-wait disabled:opacity-60"
                  >
                    {status === "submitting" ? "Sending…" : "Send enquiry"}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
